"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { useKonamiCode } from "@/hooks/useKonamiCode";
import { loadLanguage, saveLanguage, uiText, type GameLanguage, type UiTextId } from "./language";
import { PAUSE_3D } from "@/components/3D/Utils/postFxTuning";
import { bootChirp, audioContext, isMuted, setMuted, subscribeMuted, unlockAudio } from "./sound";
type GameModule = typeof import("./game");
type Phase = "idle" | "boot" | "error" | "game";
const BOOT_LINES: UiTextId[] = ["ui.cheatCodeAccepted", "ui.accessingMassifDeepSpaceNetwork", "ui.640kMemoryOk", "ui.loadingScrumExe"];

// The game chunk (Phaser + content) is only requested when the game opens.
let loading: Promise<GameModule> | null = null;
const loadGame = () => loading ??= import("./game").catch(e => {
  loading = null; // allow RETRY
  throw e;
});
export default function ScrumNebulaLauncher(t0) {
  const $ = _c(60);
  const {
    siteLanguage
  } = t0;
  const [language, setLanguage] = useState(siteLanguage === "de" ? "de" : "en");
  const [atTitle, setAtTitle] = useState(false);
  let t1;
  if ($[0] !== language) {
    t1 = text => uiText(language, text);
    $[0] = language;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t = t1;
  const [phase, setPhase] = useState("idle");
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const [lines, setLines] = useState(t2);
  const [artReady, setArtReady] = useState(false);
  const [progress, setProgress] = useState(0);
  const overlay = useRef(null);
  const host = useRef(null);
  const game = useRef(null);
  const stopBoot = useRef(null);
  const active = phase !== "idle";
  const muted = useSyncExternalStore(subscribeMuted, isMuted, _temp);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      stopBoot.current?.();
      stopBoot.current = null;
      audioContext()?.suspend().catch(_temp2);
      setPhase("idle");
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const close = t3;
  let t4;
  if ($[4] !== siteLanguage) {
    t4 = () => {
      if (overlay.current) {
        return;
      }
      const ctx = unlockAudio();
      if (ctx) {
        stopBoot.current = bootChirp(ctx);
      }
      setLines([]);
      setArtReady(false);
      setProgress(0);
      setAtTitle(false);
      setLanguage(loadLanguage(siteLanguage));
      setPhase(_temp3);
    };
    $[4] = siteLanguage;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const open = t4;
  useKonamiCode(open);
  let t5;
  let t6;
  if ($[6] !== open) {
    t5 = () => {
      const query = new URLSearchParams(window.location.search);
      if (query.has("konami") || process.env.NODE_ENV === "development" && (query.has("scrum-preview") || query.has("scrum-room"))) {
        open();
      }
    };
    t6 = [open];
    $[6] = open;
    $[7] = t5;
    $[8] = t6;
  } else {
    t5 = $[7];
    t6 = $[8];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[9] !== language || $[10] !== phase) {
    t7 = () => {
      if (phase !== "boot") {
        return;
      }
      let alive = true;
      const visibility = _temp6;
      document.addEventListener("visibilitychange", visibility);
      if (document.hidden) {
        visibility();
      }
      const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      const step = reduced ? 150 : 600;
      const timers = BOOT_LINES.map((_, i) => window.setTimeout(() => setLines(BOOT_LINES.slice(0, i + 1).map(line => uiText(language, line))), i * step));
      Promise.all([loadGame(), new Promise(r => window.setTimeout(r, BOOT_LINES.length * step))]).then(t9 => {
        const [mod] = t9;
        if (!alive) {
          return;
        }
        game.current = mod;
        setPhase("game");
      }).catch(() => {
        if (alive) {
          audioContext()?.suspend().catch(_temp7);
          setPhase("error");
        }
      });
      return () => {
        alive = false;
        timers.forEach(clearTimeout);
        document.removeEventListener("visibilitychange", visibility);
        stopBoot.current?.();
        stopBoot.current = null;
      };
    };
    t8 = [phase, language];
    $[9] = language;
    $[10] = phase;
    $[11] = t7;
    $[12] = t8;
  } else {
    t7 = $[11];
    t8 = $[12];
  }
  useEffect(t7, t8);
  let t10;
  let t9;
  if ($[13] !== language || $[14] !== phase) {
    t9 = () => {
      if (phase !== "game" || !host.current || !game.current) {
        return;
      }
      host.current.focus();
      return game.current.startGame(host.current, {
        language,
        onTitle: setAtTitle,
        onExit: close,
        onProgress: setProgress,
        onReady: () => setArtReady(true),
        onError: () => setPhase("error")
      });
    };
    t10 = [phase, close, language];
    $[13] = language;
    $[14] = phase;
    $[15] = t10;
    $[16] = t9;
  } else {
    t10 = $[15];
    t9 = $[16];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[17] !== active) {
    t11 = () => {
      if (!active || !overlay.current) {
        return;
      }
      const self = overlay.current;
      const prevFocus = document.activeElement as HTMLElement | null;
      const siblings = [...document.body.children].filter(el => el instanceof HTMLElement && el !== self && !el.inert);
      siblings.forEach(_temp8);
      const root = document.documentElement;
      const overflow = root.style.overflow;
      root.style.overflow = "hidden";
      PAUSE_3D.value = true;
      const onKey = e => {
        if (e.key !== "Escape") {
          return;
        }
        e.stopPropagation();
        close();
      };
      window.addEventListener("keydown", onKey, true);
      return () => {
        siblings.forEach(_temp9);
        if (root.style.overflow === "hidden") {
          root.style.overflow = overflow;
        }
        PAUSE_3D.value = false;
        window.removeEventListener("keydown", onKey, true);
        prevFocus?.focus();
      };
    };
    t12 = [active, close];
    $[17] = active;
    $[18] = t11;
    $[19] = t12;
  } else {
    t11 = $[18];
    t12 = $[19];
  }
  useEffect(t11, t12);
  if (!active) {
    return null;
  }
  let t13;
  if ($[20] !== lines || $[21] !== phase || $[22] !== t) {
    t13 = phase === "game" ? <div ref={host} tabIndex={-1} className="flex size-full items-center justify-center outline-none" /> : <pre className="whitespace-pre-wrap p-6 text-sm leading-relaxed">{lines.join("\n")}{phase === "error" && `\n\n${t("ui.diskReadError")}\n${t("ui.abortRetryFail")}`}</pre>;
    $[20] = lines;
    $[21] = phase;
    $[22] = t;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== artReady || $[25] !== phase || $[26] !== progress || $[27] !== t) {
    t14 = phase === "game" && !artReady && <div className="absolute inset-0 flex items-center justify-center bg-black"><p role="status">{t("ui.loadingSectorArt")} {Math.round(progress * 100)}%</p></div>;
    $[24] = artReady;
    $[25] = phase;
    $[26] = progress;
    $[27] = t;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== phase || $[30] !== t) {
    t15 = phase === "error" && <div className="flex gap-4 px-6"><button type="button" onClick={() => {
        const ctx_1 = unlockAudio();
        if (ctx_1) {
          stopBoot.current = bootChirp(ctx_1);
        }
        setLines([]);
        setArtReady(false);
        setProgress(0);
        setPhase("boot");
      }} className="underline">{t("ui.retry")}</button><button type="button" onClick={close} className="underline">{t("ui.abort")}</button></div>;
    $[29] = phase;
    $[30] = t;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== atTitle || $[33] !== language || $[34] !== phase || $[35] !== t) {
    t16 = (phase !== "game" || atTitle) && <label className="flex items-center gap-2">{t("ui.language")}<select aria-label={t("ui.language")} value={language} onKeyDown={_temp12} onChange={event_2 => {
        const next = event_2.target.value === "de" ? "de" : "en";
        saveLanguage(next);
        setLines([]);
        setArtReady(false);
        setLanguage(next);
      }} className="cursor-pointer bg-black hover:text-white"><option value="en" lang="en">English</option><option value="de" lang="de">Deutsch</option></select></label>;
    $[32] = atTitle;
    $[33] = language;
    $[34] = phase;
    $[35] = t;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== muted) {
    t17 = () => setMuted(!muted);
    $[37] = muted;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  const t18 = muted ? "ui.soundOffM" : "ui.soundOnM";
  let t19;
  if ($[39] !== t || $[40] !== t18) {
    t19 = t(t18);
    $[39] = t;
    $[40] = t18;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  let t20;
  if ($[42] !== muted || $[43] !== t17 || $[44] !== t19) {
    t20 = <button type="button" aria-pressed={muted} onClick={t17} className="hover:text-white">{t19}</button>;
    $[42] = muted;
    $[43] = t17;
    $[44] = t19;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  let t21;
  if ($[46] !== t) {
    t21 = t("ui.exitEsc");
    $[46] = t;
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  let t22;
  if ($[48] !== t21) {
    t22 = <button type="button" onClick={close} className="hover:text-white">{t21}</button>;
    $[48] = t21;
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  let t23;
  if ($[50] !== t16 || $[51] !== t20 || $[52] !== t22) {
    t23 = <div onMouseDown={_temp10} onTouchStart={_temp11} className="absolute inset-x-4 top-4 flex flex-wrap justify-end gap-x-4 gap-y-2 text-xs uppercase tracking-widest">{t16}{t20}{t22}</div>;
    $[50] = t16;
    $[51] = t20;
    $[52] = t22;
    $[53] = t23;
  } else {
    t23 = $[53];
  }
  let t24;
  if ($[54] !== language || $[55] !== t13 || $[56] !== t14 || $[57] !== t15 || $[58] !== t23) {
    t24 = <div ref={overlay} role="dialog" aria-modal="true" aria-label="The Secret of the SCRUM Nebula" lang={language} className="fixed inset-0 z-[10000] bg-black font-secondary text-[#c0c0c0]">{t13}{t14}{t15}{t23}</div>;
    $[54] = language;
    $[55] = t13;
    $[56] = t14;
    $[57] = t15;
    $[58] = t23;
    $[59] = t24;
  } else {
    t24 = $[59];
  }
  return t24;
}
function _temp12(event_1) {
  return event_1.stopPropagation();
}
function _temp11(event_0) {
  return event_0.stopPropagation();
}
function _temp10(event) {
  return event.stopPropagation();
}
function _temp9(el_1) {
  return el_1.inert = false;
}
function _temp8(el_0) {
  return el_0.inert = true;
}
function _temp7() {}
function _temp6() {
  const ctx_0 = audioContext();
  if (!ctx_0) {
    return;
  }
  if (document.hidden) {
    ctx_0.suspend().catch(_temp4);
  } else {
    ctx_0.resume().catch(_temp5);
  }
}
function _temp5() {}
function _temp4() {}
function _temp3(p) {
  return p === "idle" ? "boot" : p;
}
function _temp2() {}
function _temp() {
  return false;
}